import { defineComponent as _defineComponent } from 'vue'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const __default__ = require('@nuxtjs/composition-api').defineComponent({
  name: 'BlogPost',
  layout: 'default',
  head: {},
  computed: {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    ...require('vuex').mapGetters(['resource']),
  },
  jsonld() {
    // @ts-ignore
    return this.$jsonSchemaResource(this.resource);
  },
});

import {
  useAsync,
  useStore,
  useContext,
  useMeta,
  useRoute,
  computed,
  ref,
  onMounted,
} from '@nuxtjs/composition-api';
import algoliarecommend from '@algolia/recommend';
import { Hit } from '@algolia/client-search';
import { State } from '~/store';
import ActionButtons from '~/components/resource/ActionButtons.vue';
import CommentForm from '~/components/comment/CommentForm.vue';
import CommentList from '~/components/comment/CommentList.vue';
import ResourcesList from '~/components/resource/ResourcesList.vue';
import ResponsiveImage from '~/components/ResponsiveImage.vue';
import BlogSignUpHeader from '~/components/BlogSignUpHeader.vue';
import CountrySwitcher from '~/components/CountrySwitcher.vue';
import { ExperimentTypes } from '~/types/experiments';
import Card from '~/components/card/Card.vue';
import TBreadcrumbs from '~/components/atoms/t-breadcrumbs.vue';
import { Resource, AlgoliaResource } from '~/types/resource';
import { pluralise } from '~/utils/formatStrings';
import { daysFromNow } from '~/utils/formatDate';
import { readingTime } from '~/utils/formatText';
import AuthorToolbar from '~/components/AuthorToolbar.vue';
import LocalVersionBanner from '~/components/resource/LocalVersionBanner.vue';

interface Data {
  title?: string;
  recommendations?: Hit<AlgoliaResource>[];
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const {
  $config,
  error,
  $isControlExperiment,
  i18n,
  $intercom,
  $intercomIdentify,
  localePath,
  $hrefLangsResource,
  $metaPropertiesResource,
  $handleRedirect,
  $isFlagOn,
  $mzAnalyticsApi,
} = useContext();
const store = useStore<State>();
const route = useRoute();
const isAuthenticated = computed(() => store.getters.isAuthenticated);
const loggedInUser = computed(() => store.getters.loggedInUser);
const resource = computed<Resource | undefined>(() => store.getters.resource);

const types = ref<string[]>(['blog']);

const results = useAsync(async () => {
  const data: Data = {};
  try {
    const loadedResource = await store.dispatch('loadResource', {
      id: route.value.params.slug,
      types: types.value,
      link: route.value.path,
      country: i18n.locale,
    });

    if (!loadedResource) {
      error({ statusCode: 404, message: 'Resource not found' });
      return;
    }
    data.title = loadedResource?.meta_title || '';

    if (!$config.algolia.apiKey) {
      throw new Error('ALGOLIA_API_KEY not set');
    }
    if (!$config.algolia.applicationId) {
      throw new Error('ALGOLIA_APPLICATION_ID not set');
    }

    const recommendClient = algoliarecommend($config.algolia.applicationId, $config.algolia.apiKey);

    if (!$isControlExperiment(ExperimentTypes.BlogRecommendations)) {
      try {
        const recommendationsRes = await recommendClient.getRelatedProducts<AlgoliaResource>([
          {
            indexName: 'production_resources',
            objectID: loadedResource.old_id.toString(),
            maxRecommendations: 3,
            threshold: 30,
            fallbackParameters: { query: '', filters: `country:${i18n.locale}` },
          },
        ]);
        data.recommendations = recommendationsRes.results[0].hits;
        const recommendationsTrackingDetails = data.recommendations.map((item, index: number) => {
          return {
            Page_Id: item?.id,
            Page_Name: item?.title,
            Page_Url: $config.baseURL + item?.url,
            Position: index + 1,
          };
        });
        const eventParams = {
          Recommendedations: recommendationsTrackingDetails,
          Referrer_Page_Id: loadedResource.id,
          Referrer_Page_Name: loadedResource.name,
          Referrer_Page_Url: $config.baseURL + loadedResource.link,
        };
      } catch (err: any) {
        console.log(err);
      }
    }

    return data;
  } catch (err: any) {
    try {
      await $handleRedirect(route.value.path);
    } catch (err) {
      error({ statusCode: 404, message: err instanceof Error ? err.message : String(err) });
    }
  }
}, `blog-post-${route.value.params.slug}`);

useMeta(() => ({
  title: results?.value?.title || 'Blog post',
  meta: resource.value ? $metaPropertiesResource(resource.value) : undefined,
  link: resource.value
    ? $hrefLangsResource(
        resource.value.linked ?? [],
        resource.value?.link,
        resource.value?.country,
        route.value
      )
    : undefined,
}));

const breadcrumbs = [
  {
    name: 'Home',
    link: localePath('/'),
  },
  {
    name: 'Blog',
    link: localePath('/blog'),
  },
];

const popularBlogsParams = {
  country: i18n.locale,
  perPage: 6,
  types: ['blog'],
  orderBy: 'popularity_1_week',
};

const createEventParams = (item: Hit<AlgoliaResource>, index: number) => {
  return {
    Recommended_Page_Id: item?.id,
    Recommended_Page_Name: item?.title,
    Recommended_Page_Url: $config.baseURL + item?.url,
    Recommended_Position: index + 1,
    Referrer_Page_Id: resource.value?.id,
    Referrer_Page_Name: resource.value?.name,
    Referrer_Page_Url: $config.baseURL + resource.value?.link,
  };
};

onMounted(() => {
  setTimeout(() => {
    if (resource.value?.event_params) {
      $mzAnalyticsApi({
        event: 'pageView',
        name: 'blog',
        productArea: 'content',
        entityType: resource.value.type,
        entityId: resource.value.id,
      });
    }
    if (resource.value?.event_params_intercom) {
      const metadata = resource.value.event_params_intercom;
      if (typeof $intercom !== 'undefined') {
        $intercom.trackEvent('Blog Viewed', metadata);
        if (loggedInUser.value) {
          $intercomIdentify();
        }
      }
    }
  }, 2000);
});

return { __sfc: true,$config, error, $isControlExperiment, i18n, $intercom, $intercomIdentify, localePath, $hrefLangsResource, $metaPropertiesResource, $handleRedirect, $isFlagOn, $mzAnalyticsApi, store, route, isAuthenticated, loggedInUser, resource, types, results, breadcrumbs, popularBlogsParams, createEventParams, ActionButtons, CommentForm, CommentList, ResourcesList, ResponsiveImage, BlogSignUpHeader, CountrySwitcher, ExperimentTypes, Card, TBreadcrumbs, pluralise, daysFromNow, readingTime, AuthorToolbar, LocalVersionBanner }
}

})